import {
  isConformityControl,
  isCustomerRequestControl,
  isCustomerRequestStep,
  isCustomerStudioEntity,
} from "@/features/material-compliance/components/customer-requests/customerStudioAgentTargets"
import { isDeclarationReviewControl } from "@/features/material-compliance/components/declarations/declarationReviewAgentTargets"
import { isMasterDataEntity } from "@/features/material-compliance/components/master-data/masterDataAgentTargets"
import { isImportControl } from "@/features/material-compliance/components/master-data/masterDataImportAgentTargets"
import { isSafeInAppRoute } from "@/features/agent-chat/safeRoute"
import { transformCitationsFromApi } from "@/services/api/transformCitationsFromApi"
import { AgentAction } from "../types"

/**
 * Validate and normalize a raw `action` payload from the agent's
 * `chat.completion.action` SSE event into a typed {@link AgentAction}.
 *
 * Returns `null` for unknown action types or missing required fields so the
 * caller can ignore it gracefully (forward-compatible with newer agents).
 */
/** A non-empty string, or null — the shape every optional field here wants. */
function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null
}

export function parseAgentAction(raw: unknown): AgentAction | null {
  if (!raw || typeof raw !== "object") {
    return null
  }

  const obj = raw as Record<string, unknown>

  switch (obj.type) {
    case "navigate": {
      if (typeof obj.route === "string" && obj.route.length > 0) {
        return { type: "navigate", route: obj.route }
      }
      return null
    }
    case "highlight": {
      const targetKey = obj.targetKey ?? obj.target_key
      if (typeof targetKey === "string" && targetKey.length > 0) {
        const action: AgentAction = { type: "highlight", targetKey }
        if (typeof obj.route === "string" && obj.route.length > 0) {
          action.route = obj.route
        }
        return action
      }
      return null
    }
    case "open_questionnaire": {
      const questionnaireId =
        typeof obj.questionnaire_id === "string" ? obj.questionnaire_id : null
      const status = typeof obj.status === "string" ? obj.status : null
      if (questionnaireId && status) {
        return {
          type: "openQuestionnaire",
          questionnaireId,
          status,
          fileName: typeof obj.file_name === "string" ? obj.file_name : "",
          hasPositionData: obj.has_position_data === true,
        }
      }
      return null
    }
    case "proposal": {
      const responsePairId =
        typeof obj.responsePairId === "string"
          ? obj.responsePairId
          : typeof obj.response_pair_id === "string"
            ? obj.response_pair_id
            : null
      const questionnaireId =
        typeof obj.questionnaireId === "string"
          ? obj.questionnaireId
          : typeof obj.questionnaire_id === "string"
            ? obj.questionnaire_id
            : null
      const question = typeof obj.question === "string" ? obj.question : null
      const draftAnswer =
        typeof obj.draftAnswer === "string"
          ? obj.draftAnswer
          : typeof obj.draft_answer === "string"
            ? obj.draft_answer
            : null

      if (
        responsePairId &&
        questionnaireId &&
        question !== null &&
        draftAnswer !== null
      ) {
        return {
          type: "proposal",
          responsePairId,
          questionnaireId,
          question,
          draftAnswer,
          // The backend sends plain model_dump() (snake_case) here — this action
          // arrives inline inside chat.completion.action, not through apiClient's
          // camelCase response interceptor, so it needs the same transform the
          // separate chat.completion.citations chunk already gets.
          citations: Array.isArray(obj.citations)
            ? transformCitationsFromApi(obj.citations)
            : [],
        }
      }
      return null
    }
    case "tool_call": {
      const toolCallId =
        typeof obj.tool_call_id === "string" ? obj.tool_call_id : null
      const name = typeof obj.name === "string" ? obj.name : null
      if (toolCallId && name) {
        return {
          type: "tool_call",
          toolCallId,
          name,
          input:
            obj.input && typeof obj.input === "object"
              ? (obj.input as Record<string, unknown>)
              : {},
          // Absent on an older backend: default to requiring confirmation, so a
          // version skew fails closed (an extra prompt) rather than open (a
          // silent write).
          requiresConfirmation: obj.requires_confirmation !== false,
        }
      }
      return null
    }
    case "open_customer_studio_record": {
      const entity = obj.entity
      const recordId =
        typeof obj.record_id === "string"
          ? obj.record_id
          : typeof obj.recordId === "string"
            ? obj.recordId
            : null
      if (isCustomerStudioEntity(entity) && recordId) {
        const control = obj.control
        return {
          type: "openCustomerStudioRecord",
          entity,
          recordId,
          // Only a declaration HAS these controls — a request's live on its wizard
          // stages, so a control arriving with one would pulse a selector that
          // cannot be on that page and wait out the full element timeout before
          // blaming the user's feature flags. An unknown control is dropped the
          // same way: the destination is still right, only the highlight is not.
          control:
            entity === "conformity_declaration" && isConformityControl(control)
              ? control
              : null,
        }
      }
      return null
    }
    case "open_customer_request_step": {
      const requestId = obj.request_id ?? obj.requestId
      const step = obj.step
      // The step IS the destination here, so an unrecognised one has nowhere to
      // land — unlike a control, which only decides what gets pulsed.
      if (
        typeof requestId !== "string" ||
        requestId.length === 0 ||
        !isCustomerRequestStep(step)
      ) {
        return null
      }
      const control = obj.control
      return {
        type: "openCustomerRequestStep",
        requestId,
        step,
        control: isCustomerRequestControl(control) ? control : null,
      }
    }
    case "open_inquiry_draft": {
      const raw = obj.inquiry_ids ?? obj.inquiryIds
      // A bare string is tolerated so a single-draft hand-over from an older
      // backend still resolves rather than being dropped as unknown.
      const list = typeof raw === "string" ? [raw] : raw
      if (Array.isArray(list)) {
        const inquiryIds = list.filter(
          (id): id is string => typeof id === "string" && id.length > 0,
        )
        if (inquiryIds.length > 0) {
          // Anything but the one known stage falls back to the first-send
          // composer rather than dropping the hand-over.
          if (obj.stage === "reminder") {
            return { type: "openInquiryDraft", inquiryIds, stage: "reminder" }
          }
          return { type: "openInquiryDraft", inquiryIds }
        }
      }
      return null
    }
    case "open_inquiry": {
      const inquiryId = obj.inquiry_id ?? obj.inquiryId
      if (typeof inquiryId === "string" && inquiryId.length > 0) {
        return { type: "openInquiry", inquiryId }
      }
      return null
    }
    case "open_declaration_review": {
      const declarationId = obj.declaration_id ?? obj.declarationId
      if (typeof declarationId !== "string" || declarationId.length === 0) {
        return null
      }
      const control = obj.control
      return {
        type: "openDeclarationReview",
        declarationId,
        // An unknown control opens the review with nothing highlighted rather
        // than dropping the hand-over: the destination is still right.
        control: isDeclarationReviewControl(control) ? control : null,
        statementId: str(obj.statement_id ?? obj.statementId),
        articleNumber: str(obj.article_number ?? obj.articleNumber),
      }
    }
    case "open_help_article": {
      const articleId = str(obj.article_id ?? obj.articleId)
      const route = str(obj.route)
      // Both required. A card with no route navigates nowhere, and one with no id
      // cannot be traced back to an article, so drop rather than half-render it.
      if (!articleId || !route) return null
      // An in-app path only. The backend builds this, but the value still crosses a
      // trust boundary and it is fed straight to the router. Shared with the help
      // citation pill, which takes the same value down a different path.
      if (!isSafeInAppRoute(route)) return null
      return {
        type: "openHelpArticle",
        articleId,
        title: str(obj.title) ?? articleId,
        route,
      }
    }
    case "open_mc_import": {
      const raw = obj.file_ids ?? obj.fileIds
      // A bare string is tolerated so a single-file hand-over from an older
      // backend still resolves rather than being dropped as unknown.
      const list = typeof raw === "string" ? [raw] : raw
      if (!Array.isArray(list)) {
        return null
      }
      const filtered = list.filter(
        (id): id is string => typeof id === "string" && id.length > 0,
      )
      // Deduplicated in order, mirroring open_mc_import's own dedup on the
      // backend — belt and suspenders, since this is the one action whose
      // planning (isAtImportStage) compares fileIds by SET equality. A
      // duplicate surviving into that comparison inflates the set size on
      // one side only, which can misclassify "already on this stage" as a
      // fresh navigation or the reverse. An older backend or a malformed
      // payload cannot be assumed to have deduplicated already.
      const fileIds = Array.from(new Set(filtered))
      // The step IS the destination — a file id or the review stage — so a
      // missing one has nowhere to land, unlike a control which only decides
      // what gets pulsed.
      const step = str(obj.step)
      if (fileIds.length === 0 || !step) {
        return null
      }
      const control = obj.control
      return {
        type: "openImportStep",
        fileIds,
        step,
        control: isImportControl(control) ? control : null,
      }
    }
    case "open_master_data_record": {
      const entity = obj.entity
      const recordId =
        typeof obj.record_id === "string"
          ? obj.record_id
          : typeof obj.recordId === "string"
            ? obj.recordId
            : null
      if (isMasterDataEntity(entity) && recordId) {
        return { type: "openMasterDataRecord", entity, recordId }
      }
      return null
    }
    default:
      return null
  }
}
